import { useEffect, useMemo, useState } from 'react';
import { completeMention, findMentionSuggestions, recentChatParticipants } from './mentionSuggestions';

export const useMentionAutocomplete = ({ draft, setDraft, input, selection, messages, enabled, setError }) => {
  const [focused, setFocused] = useState(false), [cursor, setCursor] = useState({ start: 0, end: 0 });
  const [dismissed, setDismissed] = useState(null), [active, setActive] = useState({ key: '', index: 0 });
  const people = useMemo(() => recentChatParticipants(messages), [messages]);
  const context = useMemo(() => findMentionSuggestions(draft, cursor, people), [draft, cursor, people]);
  const visible = !!(enabled && focused && context && !(dismissed?.draft === draft && dismissed?.caret === cursor.start));
  const index = active.key === context?.key ? Math.min(active.index, context.matches.length - 1) : 0;
  const syncSelection = () => {
    if (!input.current) return;
    const next = { start: input.current.selectionStart, end: input.current.selectionEnd };
    selection.current = next; setCursor(next);
  };
  useEffect(() => { if (!enabled) { setFocused(false); setDismissed(null); } }, [enabled]);
  const dismiss = () => setDismissed({ draft, caret: cursor.start });
  const choose = (person) => {
    if (!context || !enabled) return;
    const next = completeMention(draft, context, person.username);
    if (next.text.length > 500) { setError('This mention would exceed the 500-character message limit.'); return; }
    setDraft(next.text); setError(''); setDismissed({ draft: next.text, caret: next.caret });
    selection.current = { start: next.caret, end: next.caret }; setCursor(selection.current);
    requestAnimationFrame(() => { input.current?.focus({ preventScroll: true }); input.current?.setSelectionRange(next.caret, next.caret); });
  };
  const onKeyDown = (event) => {
    if (!visible || event.nativeEvent.isComposing || event.keyCode === 229) return false;
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); dismiss(); return true; }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setActive({ key: context.key, index: (index + (event.key === 'ArrowDown' ? 1 : -1) + context.matches.length) % context.matches.length });
      return true;
    }
    if ((event.key === 'Enter' || event.key === 'Tab') && !event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault(); choose(context.matches[index]); return true;
    }
    return false;
  };
  return { visible, index, candidates: context?.matches || [], choose, onKeyDown, syncSelection, dismiss,
    onFocus: () => { setFocused(true); setDismissed(null); syncSelection(); }, onBlur: () => setFocused(false),
    onHover: (next) => setActive({ key: context.key, index: next }) };
};